import { AirwallexRecipientEmbeddedValues } from './airwallex';
import { DebitsBlockedReason } from '../constants/banking';
import { Nullable } from './utils';

export type BankingCurrency =
  | 'AED'
  | 'ARS'
  | 'AUD'
  | 'BDT'
  | 'BHD'
  | 'BOB'
  | 'BRL'
  | 'CAD'
  | 'CHF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'CZK'
  | 'DKK'
  | 'EGP'
  | 'EUR'
  | 'GBP'
  | 'HKD'
  | 'HRK'
  | 'HUF'
  | 'IDR'
  | 'ILS'
  | 'INR'
  | 'JPY'
  | 'KRW'
  | 'LKR'
  | 'MAD'
  | 'MXN'
  | 'MYR'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'PEN'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'RON'
  | 'SAR'
  | 'SEK'
  | 'SGD'
  | 'THB'
  | 'TRY'
  | 'USD'
  | 'UYU'
  | 'VND'
  | 'ZAR';

export type AvailableCurrencies = 'USD' | 'MXN' | 'EUR';

export type Balance = {
  availableAmount: number;
  currency: BankingCurrency;
  pendingAmount: number;
  reservedAmount: number;
  totalAmount: number;
};

export type SupportedFeature = {
  currency: BankingCurrency;
  routingCodes: { type: string; value: string }[];
  localClearingSystem?: string;
  transferMethod: string;
  type: string;
};

export type TransactionType =
  | 'DISPUTE'
  | 'DISPUTE_REVERSAL'
  | 'DISPUTE_LOST'
  | 'REFUND'
  | 'REFUND_REVERSAL'
  | 'REFUND_FAILURE'
  | 'PAYMENT_RESERVE_HOLD'
  | 'PAYMENT_RESERVE_RELEASE'
  | 'PAYOUT'
  | 'PAYOUT_FAILURE'
  | 'PAYOUT_REVERSAL'
  | 'CONVERSION_BUY'
  | 'CONVERSION_REVERSAL'
  | 'DEPOSIT'
  | 'ADJUSTMENT'
  | 'FEE'
  | 'DD_CREDIT'
  | 'DD_DEBIT'
  | 'DC_CREDIT'
  | 'DC_DEBIT'
  | 'TRANSFER'
  | 'PAYMENT'
  | 'ISSUING_AUTHORISATION_HOLD'
  | 'ISSUING_AUTHORISATION_RELEASE'
  | 'ISSUING_CAPTURE'
  | 'ISSUING_REFUND'
  | 'CARD_PURCHASE'
  | 'PURCHASE';

export type TransactionSourceType =
  | 'PAYOUT'
  | 'CONVERSION'
  | 'DEPOSIT'
  | 'ADJUSTMENT'
  | 'FEE'
  | 'PAYMENT_ATTEMPT'
  | 'REFUND'
  | 'DISPUTE'
  | 'CHARGE'
  | 'TRANSFER'
  | 'YIELD'
  | 'BATCH_PAYOUT'
  | 'CARD_PURCHASE'
  | 'CARD_REFUND'
  | 'PURCHASE'
  | 'REFUND_REVERSAL';

export type TransactionStatus = 'PENDING' | 'SETTLED' | 'CANCELLED';
export type DepositStatus = 'PENDING' | 'SETTLED' | 'REJECTED';
export type PayoutStatus =
  | 'NEW'
  | 'IN_APPROVAL'
  | 'REJECTED'
  | 'RECALLED'
  | 'BLOCKED'
  | 'PENDING_FUNDS'
  | 'CANCELLED'
  | 'IN_REVIEW'
  | 'READY_FOR_DISPATCH'
  | 'SUSPENDED'
  | 'DISPATCHED'
  | 'FAILED'
  | 'CANCELLATION_REQUESTED'
  | 'RETRIED'
  | 'SCHEDULED';

export type FinancialTransaction = {
  id: string;
  batchId: Nullable<string>;
  sourceId: string;
  fundingSourceId: Nullable<string>;
  sourceType: TransactionSourceType;
  transactionType: TransactionType;
  currency: BankingCurrency;
  amount: number;
  clientRate: Nullable<number>;
  currencyPair: Nullable<string>;
  net: number;
  fee: number;
  estimatedSettledAt: Nullable<string>;
  settledAt: string;
  description: string;
  status: TransactionStatus;
  createdAt: string;
};

export enum TransactionTypeEnum {
  CARD_TRANSACTION = 'CARD_TRANSACTION',
  PAYMENT = 'PAYMENT',
  CONVERSION = 'CONVERSION',
  DEPOSIT = 'DEPOSIT',
  CHARGE = 'CHARGE',
}

export enum OthersTransactionTypeEnum {
  CARD_PURCHASE = 'CARD_PURCHASE',
  PAYOUT = 'PAYOUT',
  TRANSFER = 'TRANSFER',
}

export type Transaction = {
  createdAt: string;
  details: string;
  amount: number;
  currency: BankingCurrency;
  status: TransactionStatus;
  transactionType: TransactionTypeEnum;
  transactionSubtype:
    | 'CONVERSION'
    | 'NORMAL'
    | 'WITH_CONVERSION'
    | 'AUTHORIZATION'
    | 'CLEARING'
    | 'REFUND'
    | 'REVERSAL'
    | 'ORIGINAL_CREDIT'
    | 'BANK_TRANSFER';
  transactionId: string;
  id: number;
};

export type FinancialTransactionsResponse = {
  transactions: FinancialTransaction[];
  hasMore: boolean;
};

export type TransactionsCountResponse = {
  count: number;
  results: Transaction[];
};

export type UnreconciledDeposit = Transaction & {
  remainingAmount: string;
  payerName: string;
  reference: string | null;
};

export type UnreconciledDepositsResponse = {
  count: number;
  results: UnreconciledDeposit[];
};

export type DepositReconciliationItem = {
  depositId: number;
  amount: string;
};

export type CreateDepositReconciliationsPayload = {
  invoiceId: string;
  reference?: string;
  deposits: DepositReconciliationItem[];
};

export type DepositReconciliation = {
  id: number;
  depositId: number;
  invoiceId: string;
  amount: string;
  reference: string;
  createdAt: string;
};

export enum ManualPaymentMethod {
  BankTransfer = 'bankTransfer',
  Cash = 'cash',
  Check = 'check',
  Other = 'other',
  CreditMemo = 'creditMemo',
}

export type CreateManualPaymentReconciliationPayload = {
  invoiceId: string;
  amount: string;
  paymentDate: string;
  paymentMethod?: ManualPaymentMethod;
  reference?: string;
  isCreditMemo?: boolean;
};

export type ManualPaymentReconciliation = {
  id: number;
  invoiceId: string;
  amount: string;
  paymentDate: string;
  paymentMethod: ManualPaymentMethod;
  reference: string;
  createdAt: string;
};

export type PaymentsCountResponse = {
  count: number;
  results: Payment[];
};

export type ConversionsCountResponse = {
  count: number;
  results: Conversion[];
};

export type TransactionsHasMoreResponse = {
  hasMore: boolean;
  transactions: Transaction[];
};

export enum ConnectedAccountStatus {
  CREATED = 'CREATED',
  SUBMITTED = 'SUBMITTED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum GlobalAccountStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  INACTIVE = 'INACTIVE',
}

export enum RFIStatus {
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  ANSWERED = 'ANSWERED',
  CLOSED = 'CLOSED',
}

export type RFI = {
  accountId: number;
  companyName: string;
  rfiType: string;
  rfiId: string;
  rfiData: any;
  status: RFIStatus;
};

export type GlobalAccountRoutingCode = {
  currency: BankingCurrency;
  type: string;
  value: string;
};

export type GlobalAccount = {
  id: number;
  globalAccountId: string;
  countryCode: string;
  mainCurrency: BankingCurrency;
  currencies: BankingCurrency[];
  status: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  iban: string;
  swiftCode: string;
  nickName: string;
  institution: {
    address?: string;
    branchName?: string;
    city?: string;
    name?: string;
    zipCode?: string;
  };
  routingCodes: GlobalAccountRoutingCode[];
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
};

export type AccountBalance = {
  currency: BankingCurrency;
  totalAmount: number;
  availableAmount: number;
  pendingAmount: number;
  globalAccounts: GlobalAccount[];
};

// A creatable global-account option for the create flow (admin + client share
// the same source). `inUse` means the account already has an active global
// account in that country; `currencies` are all the currencies it can receive.
export type GlobalAccountCreationOption = {
  countryCode: string;
  currency: BankingCurrency;
  currencies: BankingCurrency[];
  inUse: boolean;
};

export type BankingDetails = {
  id: string;
  nickname: string;
  businessName: string;
  primaryContact: string;
  createdAt: string;
  status: ConnectedAccountStatus;
  accountBalances: AccountBalance[];
  aggregatedBalance: Nullable<number>;
  rfis: Nullable<RFI[]>;
  globalAccountListSettings: Nullable<{
    accountId: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: Nullable<string>;
    settings: {
      globalAccountCurrencies: BankingCurrency[];
    };
  }>;
  marcoPayNumber?: string;
  country?: string;
  debitsBlockedAt?: Nullable<string>;
  debitsBlockedReason?: Nullable<DebitsBlockedReason>;
};

export type BankingAccount = {
  id: number;
  provider: string;
  accountId: string;
  status: string;
  isCashControlAccount: boolean;
  nickName: string;
  createdAt: string;
  updatedAt: string;
  lenderId: Nullable<number>;
  lenderName: Nullable<string>;
};

export type ConnectedAccount = {
  id: number;
  companyId: number;
  accountId: string;
  nickname: string;
  businessName: string;
  primaryContact: string;
  createdAt: string;
  status: ConnectedAccountStatus;
  globalAccounts: GlobalAccount[];
  marcoPayNumber?: string;
  balances: Nullable<Balance[]>;
  aggregatedBalance: Nullable<number>;
  rfis: Nullable<RFI[]>;
  lenderId: Nullable<number>;
  lenderName: Nullable<string>;
  feeConfig: Nullable<{ platformFee: string; monthlyFee: string }>;
  hasLowActivity?: boolean;
  debitsBlockedAt?: Nullable<string>;
  debitsBlockedReason?: Nullable<DebitsBlockedReason>;
  companyName?: Nullable<string>;
  initialUserEmail?: Nullable<string>;
  // Lender portal only: true when the lender has no preloaded beneficiary and
  // is therefore allowed to make manual transfers from this cash control account.
  canTransfer?: boolean;
  fullDetails: {
    accountDetails: {
      attachments: {
        additionalFiles: any[];
      };
      businessDetails: {
        accountUsage: {
          estimatedMonthlyRevenue: {
            amount: string;
            currency: BankingCurrency;
          };
          productReference: any[];
        };
        asTrustee: boolean;
        attachments: {
          businessDocuments: any[];
        };
        businessAddress: {
          addressLine1: string;
          countryCode: string;
          postcode: string;
          state: string;
          suburb: string;
        };
        businessIdentifiers: [
          {
            countryCode: string;
            number: string;
            type: string; // TODO: union
          },
        ];
        businessName: string;
        businessNameEnglish: Nullable<string>;
        businessNameTrading: Nullable<string>;
        businessStructure: 'OTHER'; // TODO: union
        contactNumber: null;
        descriptionOfGoodsOrServices: string;
        explanationForHighRiskCountriesExposure: Nullable<string>;
        industryCategoryCode: string;
        noShareholdersWithOver25percent: null;
        operatingCountry: string[]; // iso country codes
        registrationAddress: {
          addressLine1: string;
          countryCode: string;
          postcode: string;
          state: string;
          suburb: string;
        };
        registrationAddressEnglish: {
          addressLine1: string;
          countryCode: string;
          postcode: string;
          state: string;
          suburb: string;
        };
        url: string;
      };
      businessPersonDetails: {
        firstName: string;
        lastName: string;
        roles: (
          | 'AUTHORISED_PERSON'
          | 'BENEFICIAL_OWNER'
          | 'CONTROLLING_PERSON'
          | 'DIRECTOR'
          | 'LEGAL_REPRESENTATIVE'
        )[];
      }[];
      trusteeDetails: null;
      individualDetails: null;
      legalEntityId: string;
      legalEntityIdentifier: null;
      legalEntityType: string;
    };
    createdAt: string;
    customerAgreements: {
      agreedToDataUsage: boolean;
      agreedToTermsAndConditions: boolean;
      optInForMarketing: boolean;
    };
    id: string;
    nickname: string;
    primaryContact: {
      attachments: {
        identityFiles: any[];
      };
      email: string;
      mobile: string;
    };
    status: ConnectedAccountStatus;
    viewType: string;
  };
  globalAccountListSettings: Nullable<{
    accountId: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: Nullable<string>;
    settings: {
      globalAccountCurrencies: BankingCurrency[];
    };
  }>;
  country?: string;
};

export type ConnectedAccountsResponse = {
  results: BankingAccount[];
  count: number;
};

export type BankingMetrics = {
  totalAccounts: number;
  accountsByStatus: Record<ConnectedAccountStatus, number>;
  balanceStatistics: {
    min: number;
    max: number;
    mean: number;
    std: number;
  };
  totalBalancesByCurrency: Record<BankingCurrency, number>;
  accountsByMonth: Record<string, number>;
  activeAccountsByMonth: Record<string, number>;
  accountsByCountry: Record<string, number>; // TODO: country code instead of string
  activeAccountsByCountry: Record<string, number>; // TODO: country code instead of string
  globalAccountsByCurrency: Record<BankingCurrency, number>;
  globalAccountsByAccountType: Record<GlobalAccount['accountType'], number>;
  activeGlobalAccountsCount: number;
  activeConnectedAccountsCount: number;
  avgGlobalAccountsPerConnectedAccount: number;
  accountsWithSettledTransactions: number;
  accountsWithConvertedConversions: number;
  calculationDate: string;
  aggregatedBalances: number;
  accountsWithActivity: number;
  totalDepositCount: number;
  totalDepositAmount: number;
  accountsWithCardTransactions: number;
};

export type DepositDetail = {
  rfi?: Nullable<RfiSummary>;
  amount: number;
  createdAt: string;
  currency: BankingCurrency;
  payer?: {
    name: string;
  };
  settledAt: string;
  source?: {
    globalAccountId: string;
    type: string;
  };
  id: number;
  transactionId: string;
  sourceId?: string;
  fundingSourceId?: string;
  statementRef?: string;
  status: DepositStatus;
  type: string; // TODO: union, example BANK_TRANSFER
  relatedFinancialTransactions: FinancialTransaction[];
  reference: string;
};

export type Payment = {
  id: number;
  accountId: number;
  transactionId: string;
  amount: number;
  currency: BankingCurrency;
  status: TransactionStatus;
  lastSyncedAt: string;
  transactionType: TransactionTypeEnum;
  searchableString: string;
  details: string;
  transactionSubtype?: string;
  reference?: string;
  shortReferenceId?: string;
  requestId?: string;
  recipient?: string;
  recipientAccount?: string;
  recipientAmount: number;
  recipientCurrency: BankingCurrency;
};

export type PaymentDetail = {
  rfi?: Nullable<RfiSummary>;
  additionalInfo: any;
  amountBeneficiaryReceives: number;
  amountPayerPays: number;
  beneficiaryId: string;
  conversion: Nullable<{ currencyPair: string; rate: number }>;
  conversionId: string;
  funding: {
    status: string;
  };
  id: number;
  transactionId: string;
  metadata: {
    companyId: string;
    environment: string;
    source: string;
    userId: string;
  };
  remarks: string;
  beneficiary: {
    additionalInfo?: {
      personalEmail: string;
    };
    address: {
      city: string;
      countryCode: string;
      postcode: string;
      state: string;
      streetAddress: string;
    };
    bankDetails: {
      accountCurrency: BankingCurrency;
      accountName: string;
      accountNumber?: string;
      bankCountryCode: string;
      bankName: string;
      swiftCode?: string;
      iban?: string;
    };
    companyName?: string;
    dateOfBirth?: string;
    entityType?: string;
    firstName?: string;
    lastName?: string;
    digitalWallet?: {
      accountName: string;
      idType: string;
      idValue: string;
      provider: string;
    };
    type?: string;
  };
  createdAt: string;
  dispatchDate: string;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  feePaidBy: string; // TODO: union, example PAYER
  lastUpdatedAt: string;
  payer: {
    additionalInfo: {
      businessIncorporationDate: string;
      businessRegistrationNumber: string;
      businessRegistrationType: string;
    };
    address: {
      city: string;
      countryCode: string;
      postcode: string;
      state: string;
      streetAddress: string;
    };
    companyName: string;
    entityType: string;
  };
  paymentDate: string;
  paymentId: string;
  paymentMethod: string;
  reason: string;
  reference: string;
  requestId: string;
  selling: boolean;
  shortReferenceId: string;
  sourceAmount: number;
  sourceCurrency: BankingCurrency;
  status: PayoutStatus;
  swiftChargeOption: string;
  relatedFinancialTransactions: FinancialTransaction[];
  transferCurrency: BankingCurrency;
  paymentCurrency: BankingCurrency;
  transferAmount: number;
  transferDate: string;
  transferMethod: string;
  feesPaymentMethod: string;
  updatedAt: string;
  failureReason?: string;
  transferType: 'TRANSFER' | 'TRANSFER_REQUEST';
  transferConfig: Nullable<TransferConfig>;
  confirmedBy: Nullable<any>;
  rejectionReason: Nullable<string>;
  prepayment?: {
    amount: number;
    currency: BankingCurrency;
  };
  lockOnCreate?: boolean;
};

export type Conversion = {
  account: number;
  amount: string;
  createdAt: string;
  currency: BankingCurrency;
  currencyPair: string;
  buyCurrency: BankingCurrency;
  buyAmount: number;
  deletedAt: string;
  details: string;
  exchangeRate: string;
  id: number;
  lastSyncedAt: string;
  requestId: string;
  searchableString: string;
  shortReferenceId: string;
  status: TransactionStatus;
  transactionId: string;
  transactionType: string;
  updatedAt: string;
};

export type ConversionDetail = {
  rfi?: Nullable<RfiSummary>;
  awxRate: number;
  buyAmount: number;
  buyCurrency: BankingCurrency;
  clientRate: number;
  conversionDate: string;
  conversionId: string;
  createdAt: string;
  currencyPair: string; // example: 'USDCAD'
  dealtCurrency: BankingCurrency;
  lastUpdatedAt: string;
  midRate: number;
  quoteId: string;
  rateDetails: {
    buyAmount: number;
    level: 'AWX' | 'CLIENT';
    rate: number;
    sellAmount: number;
  }[];
  reason: string;
  requestId: string;
  sellAmount: number;
  sellCurrency: BankingCurrency;
  settlementCutoffAt: string;
  shortReferenceId: string;
  status: DepositStatus;
  relatedFinancialTransactions: FinancialTransaction[];
};

export type CardPurchaseDetail = {
  rfi?: Nullable<RfiSummary>;
  authCode: string;
  billingAmount: number;
  billingCurrency: BankingCurrency;
  cardId: string;
  cardNickname: string;
  maskedCardNumber: string;
  merchant: {
    categoryCode: string;
    city: string;
    country: string;
    name: string;
  };
  networkTransactionId: string;
  postedDate: string;
  retrievalRef: string;
  status: TransactionStatus;
  transactionAmount: number;
  transactionCurrency: BankingCurrency;
  transactionDate: string;
  transactionId: string;
  transactionType: 'AUTHORIZATION' | 'CLEARING' | 'REFUND' | 'REVERSAL' | 'ORIGINAL_CREDIT';
  relatedFinancialTransactions: FinancialTransaction[];
};

export type ChargeDetail = {
  rfi?: Nullable<RfiSummary>;
  id: number;
  amount: number;
  currency: BankingCurrency;
  fee: number;
  reason: string;
  reference: string;
  transactionId: string;
  requestId: string;
  shortReferenceId: string;
  source: string;
  status: TransactionStatus;
  createdAt: string;
  updatedAt: string;
  relatedFinancialTransactions: FinancialTransaction[];
};

export type TransactionDetail =
  | DepositDetail
  | PaymentDetail
  | ConversionDetail
  | CardPurchaseDetail
  | ChargeDetail;

export type RecentTransaction = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  transactionId: string;
  amount: string;
  currency: string;
  status: string;
  lastSyncedAt: string;
  transactionType: string;
  searchableString: string;
  details: string;
  account: number;
  companyName: string;
};

export type RecentTransactionsResponse = {
  count: number;
  next: Nullable<string>;
  previous: Nullable<string>;
  results: RecentTransaction[];
};

export enum BeneficiaryType {
  DIGITAL_WALLET = 'DIGITAL_WALLET',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
}

export type Recipient = {
  id: string;
  beneficiary: {
    additionalInfo: {
      personalEmail: string;
    };
    address: {
      city: string;
      countryCode: string;
      postcode: string;
      state: string;
      streetAddress: string;
    };
    bankDetails: {
      accountCurrency: BankingCurrency;
      accountName: string;
      accountNumber: string;
      accountRoutingType1: string;
      accountRoutingValue1: string;
      bankCountryCode: string;
      bankName: string;
      iban: string;
      localClearingSystem: string;
    };
    digitalWallet?: {
      accountName: string;
      idType: string;
      idValue: string;
      provider: string;
    };
    dateOfBirth: string;
    entityType: 'COMPANY' | 'PERSONAL';
    firstName: string;
    lastName: string;
    companyName: string;
    beneficiaryId: string;
    nickname: string;
    payerEntityType: string;
    transferMethods: string[];
  };
  nickname: string;
  payerEntityType: string;
  transferMethods: string[];
};

export interface AuthResponse {
  authorizationCode: string;
  codeVerifier: string;
  codeChallenge: string;
  scope: string[];
}

export type AirwallexRfiComponent = 'transactionRfi' | 'kycRfi' | 'paymentEnablementRfi';

// Summary of the transaction an RFI source points to (populated by the detail
// endpoint when the transaction exists in our mirror).
export interface RfiSourceTransaction {
  id: number;
  transactionId: string;
  transactionType: string;
  amount: number;
  currency: BankingCurrency;
  status: string;
  createdAt: string;
  counterparty?: Nullable<string>;
  reference?: Nullable<string>;
}

export interface RfiSource {
  id: number;
  sourceId: string;
  sourceType: string;
  answered: boolean;
  transaction?: Nullable<RfiSourceTransaction>;
}

// A single RFI question and its answer (present on the detail response only).
export interface RfiQuestion {
  id?: string;
  title?: string;
  description?: string;
  answered: boolean;
  answerType?: string;
  answerText?: string;
  answerComment?: string;
  answerConfirmed?: boolean;
}

// Full pending RFI as returned by the client RFIs endpoint.
export interface PendingRfi {
  id: number;
  rfiId: string;
  rfiType: string;
  status: string;
  component: AirwallexRfiComponent;
  sources: RfiSource[];
  createdAt: string;
  // Only populated by the detail endpoint (GET .../rfis/<id>/).
  questions?: RfiQuestion[];
  // Only populated by the admin all-accounts list: which account the RFI is on
  // (accountId opens the per-account detail drawer; companyName labels the row).
  accountId?: number;
  companyName?: Nullable<string>;
}

// Paginated response of the RFIs list endpoint.
export type RfisCountResponse = {
  count: number;
  results: PendingRfi[];
};

// Supported RFI types (those backed by an embedded component the client can resolve).
export enum RFIType {
  KYC = 'KYC',
  KYC_ONGOING = 'KYC_ONGOING',
  TRANSACTION = 'TRANSACTION',
  PAYMENT_ENABLEMENT = 'PAYMENT_ENABLEMENT',
}

// Compact RFI summary embedded in a transaction detail response (or null).
export interface RfiSummary {
  id: number;
  rfiId: string;
  rfiType: string;
  status: string;
  component: AirwallexRfiComponent;
}

export type BackendRate = {
  buyAmount: number;
  level: string;
  rate: number; // value in the weak currency
  sellAmount: number;
  validFromAt: string;
  validToAt: string;
  currencyPair: string;
  quoteId: string;
};

export type Rate = BackendRate & {
  strongCurrency: string;
  weakCurrency: string;
};

export type CardDeliveryDetails = {
  delivery_mode: CardDeliveryMode;
  delivery_vendor: CardDeliveryVendor;
  status: CardDeliveryStatus;
  status_description?: string;
  tracked: boolean;
  tracking_link?: string;
  tracking_number?: string;
  updated_at: string;
};

export enum CardDeliveryMode {
  MAIL = 'MAIL',
  EXPRESS = 'EXPRESS',
  UNKNOWN = 'UNKNOWN',
}

export enum CardDeliveryVendor {
  UNKNOWN = 'UNKNOWN',
  AU_POST = 'AU_POST',
  EMS = 'EMS',
  HK_POST = 'HK_POST',
  CITY_LINK = 'CITY_LINK',
  DHL = 'DHL',
}

export enum CardDeliveryStatus {
  PENDING = 'PENDING',
  PRINTED = 'PRINTED',
  FAILED_TO_PRINT = 'FAILED_TO_PRINT',
  DISPATCHED = 'DISPATCHED',
  IN_TRANSIT = 'IN_TRANSIT',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  DELIVERY_FAILED = 'DELIVERY_FAILED',
  DELIVERY_DELAYED = 'DELIVERY_DELAYED',
  UNKNOWN = 'UNKNOWN',
}

// This type is what Airwallex returns for a card details
// This type is used for the response of the update and create card endpoints
export type AirwallexCardDetails = {
  brand?: string;
  cardId: string;
  cardNumber?: string;
  cardStatus: CardStatus;
  cardVersion?: number;
  cardholderId?: string;
  createdAt: string;
  createdBy: string;
  deliveryDetails?: CardDeliveryDetails;
  postalAddress?: {
    city: string;
    country: string;
    line1: string;
    line2?: string;
    postcode: string;
    state: string;
  };
  formFactor?: 'VIRTUAL' | 'PHYSICAL';
  isPersonalized?: boolean;
  nameOnCard?: string;
  nickName?: string;
  program?: { purpose: string; subType: string; type: string };
  requestId?: string;
  updatedAt?: string;
  purpose?: string;
  authorizationControls?: {
    allowedCurrencies?: string[];
    allowedMerchantCategories?: string[];
    allowedTransactionCount?: string;
    transactionLimits?: {
      currency: string;
      limits: {
        amount: number;
        interval: string;
      }[];
    };
  };
};

// This type contains some extra backend fields: company name, cardholder data, limits data and id.
// This type is used just for the "get card details" endpoint
export type CardDetails = AirwallexCardDetails & {
  cardholderData?: Cardholder;
  limitsData?: { currency: string; limits: { amount: number; interval: string; remaining: number }[] };
  companyName?: string;
  id: number;
};

// This type is for the "get cards" endpoint
export type Card = {
  id: number;
  accountId: number;
  cardId: string;
  companyName?: string;
  nickName?: string;
  cardNumber?: string;
  cardType: CardFormFactorType;
  cardholder: { cardholderName: string };
  cardStatus: CardStatus;
  issuedAt: string;
  createdBy: string;
  nameOnCard: string;
  purpose?: string;
  authorizationControls?: {
    allowedCurrencies?: string[];
    allowedMerchantCategories?: string[];
    allowedTransactionCount?: string;
    transactionLimits?: {
      currency: string;
      limits: {
        amount: number;
        interval: string;
      }[];
    };
  };
};

export type CreateCardPayload = {
  limit: {
    amount: number;
    interval: string;
  };
  nickName: string;
  purpose?: string;
  isCompany: boolean;
  cardHolderId?: string;
};

export enum CardStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  CLOSED = 'CLOSED',
}

export type UpdateCardPayload = {
  limit?: {
    amount: number;
    interval: string;
  };
  nickName?: string;
  status?: CardStatus;
  purpose?: string;
};

export type RemainingLimits = {
  currency: string;
  limits: Array<{
    amount: number;
    interval: string;
    remaining: number;
  }>;
};

export type CreateCardholderPayload = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
};

export type Cardholder = {
  cardholderId: string;
  email: string;
  individual: {
    address: {
      city: string;
      country: string;
      line1: string;
      postcode: string;
      state: string;
    };
    dateOfBirth: string;
    name: {
      firstName: string;
      lastName: string;
      nameOnCard: string;
    };
  };
  status: string;
  type: string;
};

export enum CardType {
  COMPANY = 'COMPANY',
  EMPLOYEE = 'EMPLOYEE',
}

export enum CardFormFactorType {
  COMPANY = 'COMPANY',
  VIRTUAL = 'VIRTUAL',
  PHYSICAL = 'PHYSICAL',
}

export type DeleteRecipientResponse = {
  success: boolean;
};

export type GlobalAccountsResponse = {
  globalAccountBalances: AccountBalance[];
  hasMore: boolean;
};

export type CreateGlobalAccountRequest = {
  countryCode: string;
};

export type CreateGlobalAccountResponse = {
  accountName: string;
  accountNumber: string;
  accountType?: string;
  countryCode: string;
  id: string;
  institution: {
    address: string;
    city: string;
    name: string;
    zipCode?: string;
  };
  nickName: string;
  requestId: string;
  requiredFeatures: {
    currency: BankingCurrency;
    transferMethod: string;
  }[];
  status: string;
  supportedFeatures: {
    currency: BankingCurrency;
    localClearingSystem?: string;
    routingCodes: { type: string; value: string }[];
    transferMethod: string;
    type: string;
  }[];
  swiftCode?: string;
};

export type TransferFeesFilters = {
  buyCurrency: BankingCurrency;
  sellCurrency: string;
  paymentSystem: string;
  beneficiaryCountry: string;
  beneficiaryId?: string;
  transferDate?: string;
};

export type TransferFeesResponse = {
  conversionRate: null | {
    rate: number;
    validFromAt: string;
    validToAt: string;
    currencyPair: string;
    quoteId: string;
  };
  transferFeePercentage: null | number;
  transferFeeFixed: null | {
    fee: number;
  };
};

export type CreateTransferPayload = {
  feePaidBy: string;
  beneficiary?: AirwallexRecipientEmbeddedValues;
  beneficiaryId?: string;
  sourceCurrency: BankingCurrency;
  paymentDate?: string;
  paymentMethod: string;
  feesPaymentMethod: string;
  paymentCurrency: BankingCurrency;
  swiftChargeOption: string;
  sourceAmount?: number;
  paymentAmount?: number;
  reason: string;
  reference: string;
  remarks?: string;
  quoteId?: string;
};

export type TransferWithRecipientFormValues = {
  youPay: number;
  fromCurrency: Nullable<BankingCurrency>;
  recipientCurrency: BankingCurrency;
  recipientGets: number;
  recipient: Recipient;
  fromAccount: Nullable<AccountBalance>;
  coverSwiftFees: boolean;
  scheduledDate?: string;
};

export type TransferValues = {
  from: number;
  fromCurrency: BankingCurrency;
  to: number;
  toCurrency: BankingCurrency;
};

export type TransferWithBeneficiaryReviewFormValues = {
  purpose: string;
  reference: string;
  description: string;
};

export type TransferConfigUser = {
  id: number;
  name: string;
};

export interface TransferConfig {
  id: number;
  maxAmount: number;
  users: TransferConfigUser[];
  companyId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type CreateTransferConfigPayload = {
  maxAmount: number;
  users: number[];
};

export type UpdateTransferConfigPayload = {
  maxAmount: number;
  users: number[];
};

export type TransferRequest = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  beneficiary: {
    additionalInfo?: {
      personalEmail: string;
    };
    address: {
      city: string;
      countryCode: string;
      postcode: string;
      state: string;
      streetAddress: string;
    };
    bankDetails: {
      accountCurrency: BankingCurrency;
      accountName: string;
      accountNumber?: string;
      bankCountryCode: string;
      bankName: string;
      swiftCode?: string;
      iban?: string;
    };
    companyName?: string;
    dateOfBirth?: string;
    entityType?: string;
    firstName?: string;
    lastName?: string;
  };
  beneficiaryId: string;
  sourceAmount: number;
  sourceCurrency: BankingCurrency;
  paymentAmount: number;
  paymentCurrency: BankingCurrency;
  feesPaymentMethod: string;
  paymentMethod: string;
  reason: string;
  reference: string;
  remarks: string;
  status: string;
  rejectionReason?: string;
  confirmedBy: Nullable<{ id: number; name: string }>;
  transferConfig: TransferConfig;
  accountId: string;
  transferType: 'TRANSFER_REQUEST';
  amountPayerPays: number;
  amountBeneficiaryReceives: number;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  modifiedField: 'source_amount' | 'transfer_amount';
  exchangeRate: number;
  createdBy?: { id: number; name: string };
  transferDate?: string;
};

export type BankingPageTransferRequestsFilters = {
  page: number;
  pageSize: number;
  status: string;
};

export type SubmitPaymentFromTransferRequestPayload = {
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
};

// =======================
// Batch Transfers (Fees/Quotes/Submit)
// =======================

export type BatchFeesRequestItem = {
  beneficiaryId: string;
  sourceCurrency: BankingCurrency;
  paymentDate: string; // ISO string
  paymentMethod: string;
  feesPaymentMethod: string;
  paymentCurrency: BankingCurrency;
  swiftChargeOption: string;
  paymentAmount: number; // amount in paymentCurrency
  reason: string;
  reference: string;
  remarks?: string;
};

export type BatchFeesResponseItem = {
  amountBeneficiaryReceives: number;
  amountPayerPays: number;
  currencyPair: string | null;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  rate: number | null;
};

export type BatchFeesResponse = {
  batchId: number;
  items: BatchFeesResponseItem[];
  quoteValidFrom: string;
  quoteValidUntil: string;
};

export type BatchQuoteItem = {
  amountBeneficiaryReceives: number;
  amountPayerPays: number;
  currencyPair: string | null;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  rate: number | null;
};

export type BatchQuoteResponse = {
  batchId: number;
  items: BatchQuoteItem[];
  quoteValidFrom: string;
  quoteValidUntil: string;
};

export type BatchSubmitItem = {
  id: string;
  requestId: string;
  status: string;
  updatedAt: string;
  amountBeneficiaryReceives: number;
  amountPayerPays: number; // includes fees
  clientRate: number;
  currencyPair: string;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  sourceCurrency: BankingCurrency;
  transferCurrency: BankingCurrency;
};

export type BatchSubmitResponse = {
  items: BatchSubmitItem[];
  totalItemCount: number;
  validItemCount: number;
};

export type BatchTransfer = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  batchId: number;
  status: string;
  batchUpdatedAt: string;
  transferDate: string;
  totalItemCount: number;
  validItemCount: number;
  account: number;
};

export type BatchTransfersResponse = {
  count: number;
  next: Nullable<string>;
  previous: Nullable<string>;
  results: BatchTransfer[];
};

export type BankingPageBatchTransfersFilters = {
  page: number;
  pageSize: number;
};

export type BatchQuoteSummaryItem = {
  amountBeneficiaryReceives: number;
  amountPayerPays: number;
  clientRate: number;
  currencyPair: string;
  feeAmount: number;
  feeCurrency: BankingCurrency;
  sourceCurrency: BankingCurrency;
  transferCurrency: BankingCurrency;
};

export type BatchQuoteSummary = {
  expiresAt: string;
  lastQuotedAt: string;
  quotes: BatchQuoteSummaryItem[];
};

export type BatchTransferDetail = {
  funding: {
    status: string;
  };
  id: string;
  quoteSummary: BatchQuoteSummary;
  requestId: string;
  shortReferenceId: string;
  status: string;
  totalItemCount: number;
  transferDate: string;
  updatedAt: string;
  validItemCount: number;
  items: PaymentDetail[];
};

// =======================
// Recurring Payments
// =======================

export type RecurringPaymentFrequency = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
export type RecurringPaymentStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';
export type RecurringPaymentExecutionStatus = 'SUCCESS' | 'SKIPPED' | 'FAILED';

export type RecurringPaymentExecution = {
  id: number;
  scheduledDate: string;
  status: RecurringPaymentExecutionStatus;
  paymentId: Nullable<number>;
  failureReason: Nullable<string>;
  executedAt: string;
};

export type RecurringPayment = {
  id: number;
  beneficiaryId: string;
  beneficiaryName: string;
  sourceCurrency: BankingCurrency;
  paymentCurrency: BankingCurrency;
  sourceAmount: number;
  paymentMethod: string;
  feesPaymentMethod: string;
  reason: string;
  reference: string;
  remarks: string;
  swiftChargeOption: string;
  frequency: RecurringPaymentFrequency;
  startDate: string;
  nextRunDate: string;
  status: RecurringPaymentStatus;
  createdAt: string;
  updatedAt: string;
  lastExecutions: RecurringPaymentExecution[];
};

export type CreateRecurringPaymentPayload = {
  beneficiaryId: string;
  sourceCurrency: BankingCurrency;
  paymentCurrency: BankingCurrency;
  sourceAmount: number;
  paymentMethod: string;
  feesPaymentMethod: string;
  feePaidBy: string;
  swiftChargeOption: string;
  reason: string;
  reference: string;
  remarks?: string;
  frequency: RecurringPaymentFrequency;
  startDate: string;
};

export type UpdateRecurringPaymentPayload = Partial<
  Pick<
    CreateRecurringPaymentPayload,
    'sourceAmount' | 'paymentMethod' | 'feesPaymentMethod' | 'reason' | 'reference' | 'frequency'
  > & { status: RecurringPaymentStatus }
>;

export type RecurringPaymentsResponse = {
  count: number;
  next: Nullable<string>;
  previous: Nullable<string>;
  results: RecurringPayment[];
};

export type CashControlFeeConfigDetail = {
  platformFee: string;
  monthlyFee: string;
  canEditPlatformFee: boolean;
};

export type LowActivityAccount = {
  id: number;
  accountId: string;
  companyId: Nullable<number>;
  companyName: Nullable<string>;
  companyUuid: Nullable<string>;
  businessName: Nullable<string>;
  nickName: Nullable<string>;
  primaryContact: Nullable<string>;
  status: ConnectedAccountStatus;
  createdAt: Nullable<string>;
};

export enum LowActivityImportFailureReason {
  NOT_FOUND = 'NOT_FOUND',
  CASH_CONTROL = 'CASH_CONTROL',
}

export type LowActivityImportFailure = {
  accountId: string;
  reason: LowActivityImportFailureReason;
};

export type LowActivityImportResult = {
  cleared: number;
  marked: number;
  failures: LowActivityImportFailure[];
};

// The low-activity PATCH deliberately returns a narrow shape: the flag is admin-only
// and must not travel inside the full account payload.
export type LowActivityStatus = {
  id: number;
  hasLowActivity: boolean;
};

export type DebitsBlockStatus = {
  id: number;
  accountId: string;
  debitsBlockedAt: Nullable<string>;
  debitsBlockedReason: Nullable<DebitsBlockedReason>;
};
